import React, {useRef, useState} from 'react'
import {
  ActivityIndicator,
  Keyboard,
  LayoutAnimation,
  Pressable,
  type TextInput,
  View,
} from 'react-native'
import {
  BskyAgent,
  ComAtprotoServerCreateSession,
  type ComAtprotoServerDescribeServer,
} from '@atproto/api'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'

import {useRequestNotificationsPermission} from '#/lib/notifications/notifications'
import {isNetworkError} from '#/lib/strings/errors'
import {cleanError} from '#/lib/strings/errors'
import {createFullHandle} from '#/lib/strings/handles'
import {logger} from '#/logger'
import {useModalControls} from '#/state/modals'
import {useSetHasCheckedForStarterPack} from '#/state/preferences/used-starter-packs'
import {useSessionApi} from '#/state/session'
import {useLoggedOutViewControls} from '#/state/shell/logged-out'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {FormError} from '#/components/forms/FormError'
import {HostingProvider} from '#/components/forms/HostingProvider'
import * as TextField from '#/components/forms/TextField'
import {At_Stroke2_Corner0_Rounded as At} from '#/components/icons/At'
import {Lock_Stroke2_Corner0_Rounded as Lock} from '#/components/icons/Lock'
import {Ticket_Stroke2_Corner0_Rounded as Ticket} from '#/components/icons/Ticket'
import {InlineLinkText} from '#/components/Link'
import {Loader} from '#/components/Loader'
import {Text} from '#/components/Typography'
import {communityGuidelinesContent} from '#/content/community-guidelines'
import {privacyPolicyContent} from '#/content/privacy-policy'
import {termsOfServiceContent} from '#/content/terms-of-service'
import {FormContainer} from './FormContainer'

type ServiceDescription = ComAtprotoServerDescribeServer.OutputSchema

export const LoginForm = ({
  error,
  serviceUrl,
  serviceDescription,
  initialHandle,
  setError,
  setServiceUrl,
  onPressRetryConnect,
  onPressBack,
  onPressForgotPassword,
  onAttemptSuccess,
  onAttemptFailed,
}: {
  error: string
  serviceUrl: string
  serviceDescription: ServiceDescription | undefined
  initialHandle: string
  setError: (v: string) => void
  setServiceUrl: (v: string) => void
  onPressRetryConnect: () => void
  onPressBack: () => void
  onPressForgotPassword: () => void
  onAttemptSuccess: () => void
  onAttemptFailed: () => void
}) => {
  const t = useTheme()
  const [isProcessing, setIsProcessing] = useState<boolean>(false)
  const [isAuthFactorTokenNeeded, setIsAuthFactorTokenNeeded] =
    useState<boolean>(false)
  const [isAuthFactorTokenValueEmpty, setIsAuthFactorTokenValueEmpty] =
    useState<boolean>(true)
  const [inviteCodeRequired, setInviteCodeRequired] = useState<boolean>(false)
  const [inviteCodeValue, setInviteCodeValue] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('batesky_invite_code') || ''
    }
    return ''
  })
  const [isCheckingApproval, setIsCheckingApproval] = useState<boolean>(false)
  const identifierValueRef = useRef<string>(initialHandle || '')
  const passwordValueRef = useRef<string>('')
  const authFactorTokenValueRef = useRef<string>('')
  const passwordRef = useRef<TextInput>(null)
  const {_} = useLingui()
  const {login} = useSessionApi()
  const requestNotificationsPermission = useRequestNotificationsPermission()
  const {setShowLoggedOut} = useLoggedOutViewControls()
  const setHasCheckedForStarterPack = useSetHasCheckedForStarterPack()
  const {openModal} = useModalControls()

  const handleCommunityGuidelinesPress = () => {
    openModal({
      name: 'policy-document',
      title: 'Community Guidelines',
      content: communityGuidelinesContent,
    })
  }

  const handleTosPress = () => {
    openModal({
      name: 'policy-document',
      title: 'Terms of Service',
      content: termsOfServiceContent,
    })
  }

  const handlePrivacyPress = () => {
    openModal({
      name: 'policy-document',
      title: 'Privacy Policy',
      content: privacyPolicyContent,
    })
  }

  const onPressSelectService = React.useCallback(() => {
    Keyboard.dismiss()
  }, [])

  // Function to check if user is already approved
  const checkUserApproval = async (identifier: string) => {
    if (!identifier.trim() || isCheckingApproval) return

    setIsCheckingApproval(true)
    try {
      // Try to guess the handle if the user just gave their own username
      let fullIdent = identifier.toLowerCase().trim()
      if (
        !identifier.includes('@') && // not an email
        !identifier.includes('.') && // not a domain
        serviceDescription &&
        serviceDescription.availableUserDomains.length > 0
      ) {
        let matched = false
        for (const domain of serviceDescription.availableUserDomains) {
          if (fullIdent.endsWith(domain)) {
            matched = true
          }
        }
        if (!matched) {
          fullIdent = createFullHandle(
            identifier,
            serviceDescription.availableUserDomains[0],
          )
        }
      }

      // Resolve handle to DID using AT Protocol
      let userDid = fullIdent
      try {
        // If this looks like a handle (contains a dot), resolve it to DID
        if (fullIdent.includes('.') && !fullIdent.includes('@')) {
          const tempAgent = new BskyAgent({service: serviceUrl})
          const resolved = await tempAgent.com.atproto.identity.resolveHandle({
            handle: fullIdent,
          })
          userDid = resolved.data.did
          logger.debug('Resolved handle to DID', {
            handle: fullIdent,
            did: userDid,
          })
        }
      } catch (resolveError) {
        logger.debug('Failed to resolve handle to DID', {
          handle: fullIdent,
          error: resolveError,
        })
        // If resolution fails, fall back to using the handle as-is
        // The API will handle the lookup appropriately
      }

      const response = await fetch('/api/invite-system/check-approval', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({userDid}),
      })

      if (response.ok) {
        const data = await response.json()
        if (!data.isApproved) {
          LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut)
          setInviteCodeRequired(true)
        } else {
          setInviteCodeRequired(false)
        }
      }
    } catch (err) {
      logger.debug('Failed to check user approval status', {error: err})
      // On error, show invite code field to be safe
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut)
      setInviteCodeRequired(true)
    } finally {
      setIsCheckingApproval(false)
    }
  }

  // Handle password field focus
  const handlePasswordFocus = () => {
    const identifier = identifierValueRef.current
    if (identifier.trim()) {
      checkUserApproval(identifier)
    }
  }

  const onPressNext = async () => {
    if (isProcessing) return
    Keyboard.dismiss()
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut)
    setError('')

    const identifier = identifierValueRef.current.toLowerCase().trim()
    const password = passwordValueRef.current
    const authFactorToken = authFactorTokenValueRef.current
    const inviteCode = inviteCodeValue.trim()

    if (!identifier) {
      setError(_(msg`Please enter your username`))
      return
    }

    if (!password) {
      setError(_(msg`Please enter your password`))
      return
    }

    if (inviteCodeRequired && !inviteCode) {
      setError(_(msg`Please enter your invite code`))
      return
    }

    setIsProcessing(true)

    try {
      // try to guess the handle if the user just gave their own username
      let fullIdent = identifier
      if (
        !identifier.includes('@') && // not an email
        !identifier.includes('.') && // not a domain
        serviceDescription &&
        serviceDescription.availableUserDomains.length > 0
      ) {
        let matched = false
        for (const domain of serviceDescription.availableUserDomains) {
          if (fullIdent.endsWith(domain)) {
            matched = true
          }
        }
        if (!matched) {
          fullIdent = createFullHandle(
            identifier,
            serviceDescription.availableUserDomains[0],
          )
        }
      }

      // Modified login call to include invite code
      await login(
        {
          service: serviceUrl,
          identifier: fullIdent,
          password,
          authFactorToken: authFactorToken.trim(),
          inviteCode: inviteCode,
        },
        'LoginForm',
      )
      onAttemptSuccess()
      setShowLoggedOut(false)
      setHasCheckedForStarterPack(true)
      requestNotificationsPermission('Login')
    } catch (e: any) {
      const errMsg = e.toString()
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut)
      setIsProcessing(false)
      if (
        e instanceof ComAtprotoServerCreateSession.AuthFactorTokenRequiredError
      ) {
        setIsAuthFactorTokenNeeded(true)
      } else {
        onAttemptFailed()

        // Add invite code specific error handling - RESTORED MISSING HANDLER!
        if (e.message === 'INVITE_CODE_REQUIRED') {
          setInviteCodeRequired(true)
          setError(_(msg`An invite code is required to access batesky`))
        } else if (e.message?.includes('Invalid or already used invite code')) {
          setError(
            _(
              msg`Invalid or already used invite code. Please check and try again.`,
            ),
          )
        } else if (e.message?.includes('Invalid invite code')) {
          setError(_(msg`Invalid invite code. Please check and try again.`))
        } else if (errMsg.includes('Token is invalid')) {
          logger.debug('Failed to login due to invalid 2fa token', {
            error: errMsg,
          })
          setError(_(msg`Invalid 2FA confirmation code.`))
        } else if (
          errMsg.includes('Authentication Required') ||
          errMsg.includes('Invalid identifier or password')
        ) {
          logger.debug('Failed to login due to invalid credentials', {
            error: errMsg,
          })
          setError(_(msg`Incorrect username or password`))
        } else if (isNetworkError(e)) {
          logger.warn('Failed to login due to network error', {error: errMsg})
          setError(
            _(
              msg`Unable to contact your service. Please check your Internet connection.`,
            ),
          )
        } else {
          logger.warn('Failed to login', {error: errMsg})
          setError(cleanError(errMsg))
        }
      }
    }
  }

  return (
    <FormContainer testID="loginForm" titleText={<Trans>Sign in</Trans>}>
      <View style={[a.align_center, a.pb_md]}>
        <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
          <Trans>Need an account? Create one at</Trans>{' '}
          <InlineLinkText
            to="https://bsky.app"
            label={_(msg`Create an account at bsky.app`)}>
            bsky.app
          </InlineLinkText>
        </Text>
      </View>
      <View>
        <TextField.LabelText>
          <Trans>Hosting provider</Trans>
        </TextField.LabelText>
        <HostingProvider
          serviceUrl={serviceUrl}
          onSelectServiceUrl={setServiceUrl}
          onOpenDialog={onPressSelectService}
        />
      </View>
      <View>
        <TextField.LabelText>
          <Trans>Account</Trans>
        </TextField.LabelText>
        <View style={[a.gap_sm]}>
          <TextField.Root>
            <TextField.Icon icon={At} />
            <TextField.Input
              testID="loginUsernameInput"
              label={_(msg`Username or email address`)}
              autoCapitalize="none"
              autoFocus
              autoCorrect={false}
              autoComplete="username"
              returnKeyType="next"
              textContentType="username"
              defaultValue={initialHandle || ''}
              onChangeText={v => {
                identifierValueRef.current = v
              }}
              onSubmitEditing={() => {
                passwordRef.current?.focus()
              }}
              blurOnSubmit={false} // prevents flickering due to onSubmitEditing going to next field
              editable={!isProcessing}
              accessibilityHint={_(
                msg`Enter the username or email address you used when you created your account`,
              )}
            />
          </TextField.Root>

          <TextField.Root>
            <TextField.Icon icon={Lock} />
            <TextField.Input
              testID="loginPasswordInput"
              inputRef={passwordRef}
              label={_(msg`Password`)}
              autoCapitalize="none"
              autoCorrect={false}
              autoComplete="password"
              returnKeyType="done"
              enablesReturnKeyAutomatically={true}
              secureTextEntry={true}
              textContentType="password"
              clearButtonMode="while-editing"
              onChangeText={v => {
                passwordValueRef.current = v
              }}
              onFocus={handlePasswordFocus}
              onSubmitEditing={onPressNext}
              blurOnSubmit={false} // HACK: https://github.com/facebook/react-native/issues/21911#issuecomment-558343069 Keyboard blur behavior is now handled in onSubmitEditing
              editable={!isProcessing}
              accessibilityHint={_(msg`Enter your password`)}
            />
            <Button
              testID="forgotPasswordButton"
              onPress={onPressForgotPassword}
              label={_(msg`Forgot password?`)}
              accessibilityHint={_(msg`Opens password reset form`)}
              variant="solid"
              color="secondary"
              style={[
                a.rounded_sm,
                // t.atoms.bg_contrast_100,
                {marginLeft: 'auto', left: 6, padding: 6},
                a.z_10,
              ]}>
              <ButtonText>
                <Trans>Forgot?</Trans>
              </ButtonText>
            </Button>
          </TextField.Root>
        </View>
        <View style={[a.mt_sm]}>
          <Text
            style={[a.text_sm, t.atoms.text_contrast_medium, {lineHeight: 20}]}>
            <Trans>By logging in and using Batesky, you agree to our</Trans>{' '}
            <Pressable
              onPress={handleCommunityGuidelinesPress}
              accessibilityRole="button"
              accessibilityLabel={_(msg`Read the Community Guidelines`)}
              accessibilityHint={_(
                msg`Opens the Community Guidelines document`,
              )}
              style={{cursor: 'pointer'}}>
              <Text
                style={[
                  a.text_sm,
                  {
                    color: t.palette.primary_500,
                    textDecorationLine: 'underline',
                    lineHeight: 20,
                  },
                ]}>
                <Trans>Community Guidelines</Trans>
              </Text>
            </Pressable>
            <Trans>,</Trans>{' '}
            <Pressable
              onPress={handleTosPress}
              accessibilityRole="button"
              accessibilityLabel={_(msg`Read the Terms of Service`)}
              accessibilityHint={_(msg`Opens the Terms of Service document`)}
              style={{cursor: 'pointer'}}>
              <Text
                style={[
                  a.text_sm,
                  {
                    color: t.palette.primary_500,
                    textDecorationLine: 'underline',
                    lineHeight: 20,
                  },
                ]}>
                <Trans>Terms of Service</Trans>
              </Text>
            </Pressable>{' '}
            <Trans>and</Trans>{' '}
            <Pressable
              onPress={handlePrivacyPress}
              accessibilityRole="button"
              accessibilityLabel={_(msg`Read the Privacy Policy`)}
              accessibilityHint={_(msg`Opens the Privacy Policy document`)}
              style={{cursor: 'pointer'}}>
              <Text
                style={[
                  a.text_sm,
                  {
                    color: t.palette.primary_500,
                    textDecorationLine: 'underline',
                    lineHeight: 20,
                  },
                ]}>
                <Trans>Privacy Policy</Trans>
              </Text>
            </Pressable>
            <Trans>, and confirm that you are at least 18 years old.</Trans>
          </Text>
        </View>
      </View>
      {inviteCodeRequired && (
        <View>
          <TextField.LabelText>
            <Trans>Invite Code</Trans>
          </TextField.LabelText>
          <TextField.Root>
            <TextField.Icon icon={Ticket} />
            <TextField.Input
              testID="loginInviteCodeInput"
              label={_(msg`Enter your invite code`)}
              autoCapitalize="none"
              autoCorrect={false}
              returnKeyType="done"
              value={inviteCodeValue}
              onChangeText={setInviteCodeValue}
              onSubmitEditing={onPressNext}
              editable={!isProcessing}
              accessibilityHint={_(msg`Enter the invite code you received`)}
              placeholder={_(msg`e.g., ocean-sunset-jazz`)}
            />
          </TextField.Root>
          <Text style={[a.text_sm, t.atoms.text_contrast_medium, a.mt_sm]}>
            <Trans>Enter the invite code you received to access Batesky.</Trans>
          </Text>
        </View>
      )}
      {isAuthFactorTokenNeeded && (
        <View>
          <TextField.LabelText>
            <Trans>2FA Confirmation</Trans>
          </TextField.LabelText>
          <TextField.Root>
            <TextField.Icon icon={Ticket} />
            <TextField.Input
              testID="loginAuthFactorTokenInput"
              label={_(msg`Confirmation code`)}
              autoCapitalize="none"
              autoFocus
              autoCorrect={false}
              autoComplete="one-time-code"
              returnKeyType="done"
              textContentType="username"
              blurOnSubmit={false} // prevents flickering due to onSubmitEditing going to next field
              onChangeText={v => {
                setIsAuthFactorTokenValueEmpty(v === '')
                authFactorTokenValueRef.current = v
              }}
              onSubmitEditing={onPressNext}
              editable={!isProcessing}
              accessibilityHint={_(
                msg`Input the code which has been emailed to you`,
              )}
              style={[
                {
                  textTransform: isAuthFactorTokenValueEmpty
                    ? 'none'
                    : 'uppercase',
                },
              ]}
            />
          </TextField.Root>
          <Text style={[a.text_sm, t.atoms.text_contrast_medium, a.mt_sm]}>
            <Trans>
              Check your email for a sign in code and enter it here.
            </Trans>
          </Text>
        </View>
      )}
      <FormError error={error} />
      <View style={[a.flex_row, a.align_center, a.pt_md]}>
        <Button
          label={_(msg`Back`)}
          variant="solid"
          color="secondary"
          size="large"
          onPress={onPressBack}>
          <ButtonText>
            <Trans>Back</Trans>
          </ButtonText>
        </Button>
        <View style={a.flex_1} />
        {!serviceDescription && error ? (
          <Button
            testID="loginRetryButton"
            label={_(msg`Retry`)}
            accessibilityHint={_(msg`Retries signing in`)}
            variant="solid"
            color="secondary"
            size="large"
            onPress={onPressRetryConnect}>
            <ButtonText>
              <Trans>Retry</Trans>
            </ButtonText>
          </Button>
        ) : !serviceDescription ? (
          <>
            <ActivityIndicator color="#FD7C21" />
            <Text style={[t.atoms.text_contrast_high, a.pl_md]}>
              <Trans>Connecting...</Trans>
            </Text>
          </>
        ) : (
          <Button
            testID="loginNextButton"
            label={_(msg`Next`)}
            accessibilityHint={_(msg`Navigates to the next screen`)}
            variant="solid"
            color="primary"
            size="large"
            onPress={onPressNext}>
            <ButtonText>
              <Trans>Next</Trans>
            </ButtonText>
            {isProcessing && <ButtonIcon icon={Loader} />}
          </Button>
        )}
      </View>
      <View style={[a.pt_md, a.align_center]}>
        <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
          <Trans>Need an invite code? DM</Trans>{' '}
          <a
            href="https://bsky.app/profile/batesky.app"
            target="_blank"
            rel="noopener noreferrer"
            style={{color: t.palette.primary_500}}>
            <Text style={{color: t.palette.primary_500}}>@batesky.app</Text>
          </a>
        </Text>
      </View>
    </FormContainer>
  )
}
